import {
  TrackingElementContext,
  TrackingProductStreamElementContext,
  TrackingSectionContext,
  TrackingStreamSectionContext,
  TrackingWishlistItem,
  TrackingWishlistItemId,
} from '../../../types';
import {getCategory} from '../helpers/category';
import {
  DataLayerProductGA4,
  DetailedPageInfo,
  EventPageData,
  InitialPageInfo,
  TrackingClientStateHolder,
} from '../helpers/clientState';
import {shouldPushToDataLayerObject} from '../helpers/datalayer';
import {exists} from '../helpers/exists';
import {debugWarn} from '../helpers/log';
import {getStateFullDataLayerProduct} from '../helpers/product';
import {trackWishlist} from './trackWishlist';

export interface AddToWishListEventGA4 extends EventPageData {
  event: 'add_to_wishlist';
  ecommerce: {
    items: DataLayerProductGA4[];
  };
}
export interface RemoveFromWishListEventGA4 extends EventPageData {
  event: 'remove_from_wishlist';
  ecommerce: {
    items: DataLayerProductGA4[];
  };
}

export type WishListActionEvent = {
  GA4: AddToWishListEventGA4 | RemoveFromWishListEventGA4;
};

export const trackWishlistAction =
  ({
    type,
    section,
    element,
    wishlistItem,
  }: {
    type: 'add-to-wishlist' | 'removed-from-wishlist';
    section: TrackingSectionContext;
    element: TrackingElementContext | TrackingProductStreamElementContext;
    wishlistItem: TrackingWishlistItem;
  }) =>
  (clientState: TrackingClientStateHolder) => {
    let currentState = clientState.getCurrent();
    const {currentPage} = currentState;

    if (!currentPage || currentPage.type === 'initial') {
      debugWarn('tried track log wishlist action before page view');
      return;
    }

    const product = wishlistItem.product;
    const variant = wishlistItem.variant;
    const category = getCategory({
      productCategories: null,
      state: currentState,
    });

    const {product: productGA} = getStateFullDataLayerProduct({
      product,
      variant,
      currentState,
      element,
      section,
      category,
      productUnitPrice: product.unitPrice,
      sortChannel: (section as TrackingStreamSectionContext).sortChannel,
    });

    currentState = updateWishlistState({
      type,
      wishlistItem,
      clientState,
    });

    const event = createEvent({
      type,
      products: {
        GA4: [productGA],
      },
      currentPage,
    });

    const currentStateWishlistItems = currentState.wishlist?.items;

    const wishlistProducts = {
      GA4: trackWishlist(currentStateWishlistItems),
      GA3: undefined,
    };

    shouldPushToDataLayerObject(wishlistProducts, clientState);

    return event;
  };

function createEvent({
  type,
  products,
  currentPage,
}: {
  type: 'add-to-wishlist' | 'removed-from-wishlist';
  products: {
    GA4: DataLayerProductGA4[];
  };
  currentPage?: DetailedPageInfo | InitialPageInfo;
}) {
  const isAddAction = type === 'add-to-wishlist';

  const eventGA4 = {
    event: isAddAction ? 'add_to_wishlist' : 'remove_from_wishlist',
    content_name: currentPage?.page?.path || '',
    page_type: currentPage?.page?.type || '',
    page_type_id:
      currentPage?.type === 'detailed' ? `${currentPage.page?.id || ''}` : '',
    ecommerce: {
      items: products.GA4,
    },
  } as AddToWishListEventGA4 | RemoveFromWishListEventGA4;

  return {
    GA4: eventGA4,
  };
}

const updateWishlistState = ({
  type,
  wishlistItem,
  clientState,
}: {
  type: 'add-to-wishlist' | 'removed-from-wishlist';
  wishlistItem: TrackingWishlistItem;
  clientState: TrackingClientStateHolder;
}) => {
  const currentState = clientState.getCurrent();
  if (type === 'removed-from-wishlist') {
    const filteredWishlistItems = currentState.wishlist?.items.filter(
      item => item.item_id !== wishlistItem.product.id
    );

    return clientState.update({
      wishlist: {
        items: filteredWishlistItems,
      },
    });
  } else {
    const wishlistItemAlreadyExists = exists(
      currentState.wishlist?.items.find(
        item => item.item_id === wishlistItem.product.id
      )
    );
    let updatedWishlistItems: TrackingWishlistItemId[];

    if (wishlistItemAlreadyExists) {
      updatedWishlistItems = currentState.wishlist?.items.map(item =>
        item.item_id === wishlistItem.product.id
          ? {item_id: wishlistItem.product.id}
          : item
      );
    } else {
      updatedWishlistItems = currentState.wishlist?.items.concat({
        item_id: wishlistItem.product.id,
      });
    }

    return clientState.update({
      wishlist: {
        items: updatedWishlistItems,
      },
    });
  }
};
